import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INavLink, Nav } from '@fluentui/react';
import { IWithErrorHandlingProps } from './withErrorHandling';
import { OrbUserContext } from './OrbUserContext';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import { darkBlueTheme } from '../AppGlobals';
import { DataContext } from './DataContext';
import { _onRenderNavLink } from './ZedXMainMenu';
import { App03Frameworks } from '../types/App03Workflow';
import { LoggedUserInfoForMenu } from './LoggedUserInfoForMenu';
import { IEntityDescription1 } from '../types';
import { TruncatedText } from './TruncatedText';
import { SprintFilterCategoryKeys } from './app03Workflows/WorkflowTasks';
import { decodeRouteId } from '../utils/routeId';

interface IAgileWorkStreamsMenu extends IWithErrorHandlingProps {
    onLinkClicked?: () => void;
    frameworkID: App03Frameworks;
    entityDescriptionForMenu: IEntityDescription1;
    onWorkItemsFilterChange?: (filter: string) => void;
    selectedWorkItemsFilter: string | null;
}

type ParentTypes = 'portfolio' | 'project' | 'workstream' | 'template';
type ChildView = 'projects' | 'workstreams' | 'tasks' | 'epics' | 'sprints' | 'workflow-users' | 'work-item-types' | 'ask-cloe' | 'none';

export const AgileWorkStreamsMenu = ({
    frameworkID,
    entityDescriptionForMenu,
    onWorkItemsFilterChange,
    selectedWorkItemsFilter,
    errorHandling: { clearErrors },
    onLinkClicked
}: IAgileWorkStreamsMenu): React.ReactElement => {

    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;
    const { userPermissions: up } = useContext(OrbUserContext);
    const history = useHistory();
    const { pathname } = useLocation();
    const { dataServices } = useContext(DataContext);
    const [lastParentId, setLastParentId] = useState<number | null>(null);

    const [navs, setNavs] = useState<INavLink[]>([]);
    const resetErrors = useCallback(clearErrors, [clearErrors]);

    const [totalPortfoliosCount, setTotalPortfoliosCount] = useState(0);
    const [totalProjectsCount, setTotalProjectsCount] = useState(0);
    const [totalWorkflowsCount, setTotalWorkflowsCount] = useState(0);
    const [settingsExpanded, setSettingsExpanded] = useState(false);
    const hasInitialisedFromRoute = useRef(false);

    const countTotalPortfolios = () => {
        dataServices.app03PortfolioService.countAllForList(frameworkID).then(p => {
            setTotalPortfoliosCount(p.length ?? 0);
        }).catch(() => { setTotalPortfoliosCount(0); });
    };

    const countTotalProjects = () => {
        dataServices.app03ProjectService.countAllForList(frameworkID).then(p => {
            setTotalProjectsCount(p.length ?? 0);
        }).catch(() => { setTotalProjectsCount(0); });
    };

    const countTotalWorkflows = () => {
        dataServices.app03WorkflowService.countAllForList(frameworkID).then(wfs => {
            setTotalWorkflowsCount(wfs.length ?? 0);
        }).catch(() => { setTotalWorkflowsCount(0); });
    };

    const handleFilterClick = (filterKey: string) => {
        onWorkItemsFilterChange?.(filterKey);
        if (onLinkClicked) onLinkClicked();
    };


    useEffect(() => {
        countTotalPortfolios();
        countTotalProjects();
        countTotalWorkflows();
    }, [frameworkID, dataServices]);

    // -----------------------------------------
    // parseMenuMode now returns contextual flags + child view type
    // -----------------------------------------
    const parseMenuMode = (path: string): {
        isContextualMenu: boolean;
        parentType?: ParentTypes;
        parentId?: number;
        childView?: ChildView;
    } => {

        // projects route for a portfolio (child view = projects)
        const projectsRegex = /^\/a10\/cwf-projects-for-portfolio\/([^/]+)/;
        const matchProjects = path.match(projectsRegex);
        if (matchProjects) {
            return { isContextualMenu: true, parentType: "portfolio", parentId: decodeRouteId(matchProjects[1]), childView: 'projects' };
        }

        // workstreams route for a portfolio (child view = workstreams)
        const workstreamsRegex = /^\/a10\/workflows-for-portfolio\/([^/]+)/;
        const matchWorkstreaams = path.match(workstreamsRegex);
        if (matchWorkstreaams) {
            return { isContextualMenu: true, parentType: "portfolio", parentId: decodeRouteId(matchWorkstreaams[1]), childView: 'workstreams' };
        }

        // workstreams route for a project (child view = workstreams)
        const workstreamsProjectRegex = /^\/a10\/workflows-for-project\/([^/]+)/;
        const matchWorkstreaamsForProject = path.match(workstreamsProjectRegex);
        if (matchWorkstreaamsForProject) {
            return { isContextualMenu: true, parentType: "project", parentId: decodeRouteId(matchWorkstreaamsForProject[1]), childView: 'workstreams' };
        }

        // workflow tasks contextual mode (child view = tasks)
        const workflowTasksRegex = /^\/a10\/workflow-tasks\/([^/]+)/;
        const matchWorkflowTasks = path.match(workflowTasksRegex);
        if (matchWorkflowTasks) {
            return { isContextualMenu: true, parentType: "workstream", parentId: decodeRouteId(matchWorkflowTasks[1]), childView: 'tasks' };
        }

        // epics route for a workstream (child view = epics)
        const epicsRegex = /^\/a10\/epics\/([^/]+)/;
        const matchEpics = path.match(epicsRegex);
        if (matchEpics) {
            return { isContextualMenu: true, parentType: "workstream", parentId: decodeRouteId(matchEpics[1]), childView: 'epics' };
        }

        // sprints route for a workstream (child view = sprints)
        const sprintsRegex = /^\/a10\/sprints\/([^/]+)/;
        const matchSprints = path.match(sprintsRegex);
        if (matchSprints) {
            return { isContextualMenu: true, parentType: "workstream", parentId: decodeRouteId(matchSprints[1]), childView: 'sprints' };
        }

        // workflow-users route for a workstream
        const usersRegex = /^\/a10\/workflow-users\/([^/]+)/;
        const matchUsers = path.match(usersRegex);
        if (matchUsers) {
            return {
                isContextualMenu: true,
                parentType: "workstream",
                parentId: decodeRouteId(matchUsers[1]),
                childView: 'workflow-users'
            };
        }

        // work item types route for a template
        const workItemTypesRegex = /^\/a10\/settings\/work-item-types\/([^/]+)/;
        const matchWorkItemTypes = path.match(workItemTypesRegex);
        if (matchWorkItemTypes) {
            return {
                isContextualMenu: true,
                parentType: "template",
                parentId: decodeRouteId(matchWorkItemTypes[1]),
                childView: 'work-item-types'
            };
        }

        // ask cloe contextual mode
        const askCloeRegex = /^\/a10\/ask-cloe\/([^/]+)/;
        if (askCloeRegex.test(path)) {
            return { isContextualMenu: true, childView: 'ask-cloe' };
        }

        // default - not contextual
        return { isContextualMenu: false, childView: 'none' };
    };

    const { isContextualMenu, parentType, parentId, childView } = parseMenuMode(pathname);

    // -----------------------------------------
    // Reset sprint filter behavior:
    // - Only reset when entering/leaving workflow-tasks child (tasks view), or when not contextual
    // - Don't reset when changing between childViews that are NOT tasks (e.g., tasks -> epics) unless you want that
    // -----------------------------------------
    useEffect(() => {
        // If we're in a workstream 'tasks' view and the parentId changed, reset filter to Current Sprint
        if (isContextualMenu && parentType === "workstream" && childView === 'tasks' && parentId != null) {
            if (parentId !== lastParentId) {
                // Reset filter to Current Sprint
                //console.log('resetting filter to current sprint');
                onWorkItemsFilterChange?.(SprintFilterCategoryKeys.CurrentSprint);
                setLastParentId(parentId);
            }
            return;
        }

        // If we left contextual mode entirely, reset to Current Sprint as before
        if (!isContextualMenu) {
            onWorkItemsFilterChange?.(SprintFilterCategoryKeys.CurrentSprint);
            setLastParentId(null);
            return;
        }

        // If we switched to a workstream child that is NOT tasks (e.g., epics), we *do not* force-reset the tasks filter.
        // If you want a different behavior (e.g., clear filter when going to epics), handle it here.
    }, [parentId, lastParentId, isContextualMenu, parentType, childView, onWorkItemsFilterChange]);

    /* -------------------------------------------------------
       One-time auto-expand based on initial route (GLOBAL MENU)
    -------------------------------------------------------- */
    useEffect(() => {
        if (hasInitialisedFromRoute.current || isContextualMenu) return;

        if (
            pathname.startsWith('/a10/settings/templates')
        ) {
            setSettingsExpanded(true);
        }

        hasInitialisedFromRoute.current = true;
    }, [pathname, isContextualMenu]);

    // Build navigation links depending on mode & childView
    useEffect(() => {

        const routerNav = (path: string): void => {
            history.push(path);
            if (onLinkClicked) onLinkClicked();
        };

        const NamePathToNavLink = ({ name, path }: { name: string; path: string }): INavLink => {
            return {
                key: path,
                name: name,
                url: `#${path}`,
                onClick: () => routerNav(path)
            };
        };

        let links: INavLink[] = [];

        // -------------------------------------
        // CONTEXTUAL: ASK CLOE
        // -------------------------------------
        if (isContextualMenu && childView === 'ask-cloe') {
            links = [
                { name: 'Home', path: '/' },
                { name: 'Welcome', path: '/a10/welcome' },
                { name: 'Cloe Chat', path: pathname }
            ].map(NamePathToNavLink);

            setNavs(links);
            return;
        }

        // -------------------------------------
        // CONTEXTUAL: WORKSTREAM CASE
        // -------------------------------------

        if (isContextualMenu && parentType === "workstream" && parentId != null) {
            //console.log('entityDescriptionForMenu', entityDescriptionForMenu);

            // --- TASKS MODE: Work Item filters ---
            if (childView === 'tasks') {
                links = [
                    NamePathToNavLink({ name: "Home", path: "/" }),
                    NamePathToNavLink({ name: "Welcome", path: "/a10/welcome" }),
                    {
                        key: "work-items-parent",
                        name: "Work Items",
                        url: null,
                        isExpanded: true,
                        links: [
                            {
                                key: SprintFilterCategoryKeys.Backlog,
                                name: "Backlog",
                                url: "",
                                onClick: () => handleFilterClick(SprintFilterCategoryKeys.Backlog)
                            },
                            {
                                key: SprintFilterCategoryKeys.CurrentSprint,
                                name: "Current Sprint",
                                url: "",
                                onClick: () => handleFilterClick(SprintFilterCategoryKeys.CurrentSprint)
                            },
                            {
                                key: SprintFilterCategoryKeys.PlannedSprint,
                                name: "Planned Sprints",
                                url: "",
                                onClick: () => handleFilterClick(SprintFilterCategoryKeys.PlannedSprint)
                            },
                            {
                                key: SprintFilterCategoryKeys.HistoricSprint,
                                name: "Historic Sprints",
                                url: "",
                                onClick: () => handleFilterClick(SprintFilterCategoryKeys.HistoricSprint)
                            },
                            {
                                key: SprintFilterCategoryKeys.Epic,
                                name: "Epics",
                                url: "",
                                onClick: () => handleFilterClick(SprintFilterCategoryKeys.Epic)
                            },
                            {
                                key: SprintFilterCategoryKeys.Archived,
                                name: "Archived",
                                url: "",
                                onClick: () => handleFilterClick(SprintFilterCategoryKeys.Archived)
                            }
                        ]
                    }
                ];

                setNavs(links);
                return;
            }

            // --- EPICS MODE
            if (childView === 'epics') {
                links = [
                    NamePathToNavLink({ name: "Home", path: "/" }),
                    NamePathToNavLink({ name: "Welcome", path: "/a10/welcome" }),
                    NamePathToNavLink({
                        name: "Epics",
                        path: pathname
                    })
                ];

                setNavs(links);
                return;
            }

            // --- SPRINTS MODE
            if (childView === 'sprints') {
                links = [
                    NamePathToNavLink({ name: "Home", path: "/" }),
                    NamePathToNavLink({ name: "Welcome", path: "/a10/welcome" }),
                    NamePathToNavLink({
                        name: "Sprints",
                        path: pathname
                    })
                ];

                setNavs(links);
                return;
            }

            // --- WORKFLOW USERS MODE
            if (childView === 'workflow-users') {
                links = [
                    NamePathToNavLink({ name: "Home", path: "/" }),
                    NamePathToNavLink({ name: "Welcome", path: "/a10/welcome" }),
                    NamePathToNavLink({
                        name: "Workstream Users",
                        path: pathname
                    })
                ];

                setNavs(links);
                return;
            }

        }

        // -------------------------------------
        // CONTEXTUAL: TEMPLATE CASE
        // -------------------------------------
        if (isContextualMenu && parentType === "template" && parentId != null) {

            if (childView === 'work-item-types') {
                links = [
                    NamePathToNavLink({ name: "Home", path: "/" }),
                    NamePathToNavLink({ name: "Welcome", path: "/a10/welcome" }),
                    NamePathToNavLink({
                        name: "Work Item Types",
                        path: pathname
                    })
                ];

                setNavs(links);
                return;
            }
        }

        // -------------------------------------
        // CONTEXTUAL: PORTFOLIO CASE
        // -------------------------------------
        if (isContextualMenu && parentType === "portfolio" && parentId != null) {

            // --- Projects MODE
            if (childView === 'projects') {
                links = [
                    { name: 'Home', path: '/' },
                    { name: 'Welcome', path: '/a10/welcome' },
                    {
                        name: 'Projects',
                        path: pathname
                    }
                ].map(NamePathToNavLink);

                setNavs(links);
                return;
            }

            // --- WORKFLOW MODE
            if (childView === 'workstreams') {
                links = [
                    { name: 'Home', path: '/' },
                    { name: 'Welcome', path: '/a10/welcome' },
                    {
                        name: 'Workstreams',
                        path: pathname
                    }
                ].map(NamePathToNavLink);

                setNavs(links);
                return;
            }
        }


        // -------------------------------------
        // CONTEXTUAL: PROJECT CASE
        // -------------------------------------
        if (isContextualMenu && parentType === "project" && parentId != null) {

            // --- WORKFLOW MODE
            if (childView === 'workstreams') {
                links = [
                    { name: 'Home', path: '/' },
                    { name: 'Welcome', path: '/a10/welcome' },
                    {
                        name: 'Workstreams',
                        path: pathname
                    }
                ].map(NamePathToNavLink);

                setNavs(links);
                return;
            }
        }

        // -------------------------------------
        // GLOBAL MENU
        // -------------------------------------
        links = [
            { name: 'Home', path: '/' },
            { name: 'Welcome', path: '/a10/welcome' },
            { name: 'Insights', path: '/a10/insights' },
            { name: 'Ask Cloe', path: '/a10/ai-chats' },
            { name: 'Portfolios', path: '/a10/cwf-portfolios' },
            { name: 'Projects', path: '/a10/cwf-projects' },
            { name: 'Workstreams', path: '/a10/workflows' }
        ].map(NamePathToNavLink);

        if (up.UserIsSystemAdmin()) {
            links.push({
                key: 'as_settings',
                name: 'Settings',
                url: null,
                isExpanded: settingsExpanded,
                onClick: () => setSettingsExpanded(prev => !prev),
                links: [
                    { name: 'Templates', path: '/a10/settings/templates' },
                ].map(NamePathToNavLink)
            });
        }

        setNavs(links);

    }, [up, history, onLinkClicked, resetErrors, pathname, isContextualMenu, parentType, parentId, childView]);

    // determine selectedKey for Nav
    const selectedKey = (() => {
        if (isContextualMenu && parentType === "workstream") {
            if (childView === 'tasks') {
                return selectedWorkItemsFilter ?? undefined;
            }
            // fallback to pathname keying if no child-specific selectedKey
            return pathname;
        }
        // non-contextual
        return pathname;
    })();

    return (
        <ThemeProvider theme={darkBlueTheme}>
            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>

                {/* Logo */}
                <div style={{
                    width: "100%",
                    paddingTop: "0",
                    paddingBottom: "1px",
                    paddingLeft: '15px',
                    display: "flex",
                    alignItems: "center"
                }}>
                    <img
                        src={`${imagesRoot}/leftnav/LeftMenu-AgileSprints.png`}
                        alt="Agile Sprints"
                        style={{ maxWidth: "100%", height: "auto" }}
                    />
                </div>

                {/* Logged-in user */}
                <LoggedUserInfoForMenu />

                {/* Contextual header info */}
                {isContextualMenu && entityDescriptionForMenu && (
                    <div style={{
                        backgroundColor: '#1A619F',
                        color: 'white',
                        padding: '10px 15px',
                        marginTop: '5px',
                        marginBottom: '10px',
                        marginLeft: '10px',
                        marginRight: '10px',
                        borderRadius: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                    }}>
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine1} />
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine2} />
                    </div>
                )}

                {/* Navigation */}
                <Nav
                    selectedKey={selectedKey}
                    groups={[{ links: navs }]}
                    onRenderLink={(lnk) =>
                        _onRenderNavLink(
                            lnk,
                            totalWorkflowsCount,
                            totalPortfoliosCount,
                            totalProjectsCount
                        )
                    }
                />

            </div>
        </ThemeProvider>
    );
};